import * as React from 'react';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useEffect, useState} from "react";
import merchants from '../../models/merchants'
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {
    Add,
    Search,
    Refresh,
    RestartAlt,
    Check,
    FormatListBulleted,
    People,
    BusinessCenter, Visibility
} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {Link as RouterLink, useNavigate, useSearchParams} from "react-router-dom";
import {Chip, Stack, TextField, Tooltip, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import Pagination from "../Pagination";
import {AclRouterLink} from "../ACL";
import IconButton from "@mui/material/IconButton";
import PageHeader from '../layout/PageHeader';
import FilterPanel from '../layout/FilterPanel';
import SectionCard from '../layout/SectionCard';
import TableToolbar from '../layout/TableToolbar';

const hasPaymentsAccess = (row) => row?.can_create_payments === true || row?.can_create_payments === 1

const parseDailyLimit = (row) => {
    const v = row?.max_daily_volume != null ? parseFloat(row.max_daily_volume) : null
    if (v == null || Number.isNaN(v) || v <= 0) return null
    return v
}

const formatAmount = (amount, symbol) => {
    const formatted = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    return symbol ? `${formatted} ${symbol}` : formatted
}

const formatDailyLimit = (row, symbol) => {
    const v = parseDailyLimit(row)
    if (v == null) return 'Unlimited'
    return formatAmount(v, symbol)
}

const sumEnabledDailyLimits = (list) => {
    if (!Array.isArray(list)) return 0
    return list.reduce((sum, row) => {
        if (!hasPaymentsAccess(row)) return sum
        const v = parseDailyLimit(row)
        return v == null ? sum : sum + v
    }, 0)
}

const PaymentsKpi = ({ title, value }) => (
    <Box
        sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.default',
            px: 1.5,
            py: 1.25,
            minHeight: 64,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}
    >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.02em' }}>
            {title}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {value}
        </Typography>
    </Box>
)

export default function MerchantIndex() {
    const [searchParams, setSearchParams] = useSearchParams()
    const [refreshId, setRefreshId] = useState(0)
    const [merchantsData, setMerchantsData] = useState(null)
    const {setShowLoader, setForcedMerchant, defaultCurrency} = useRootHook()
    const navigate = useNavigate()
    const accordionExpanded = Array.from(searchParams).length > 0
    const currencySymbol = defaultCurrency?.symbol || defaultCurrency?.currency || ''

    useEffect(() => {
        setShowLoader(true)
        const filters = searchParams ? Object.fromEntries(searchParams) : {}
        merchants.getData(filters)
            .then((data) => {
                data && setMerchantsData(data)
                setShowLoader(false)
            }).catch(() => setShowLoader(false))
    }, [searchParams, refreshId])

    const handleSetForceMerchantID = (merchantID) => {
        setForcedMerchant(merchantID)
        navigate('/?force_merchant_id=' + merchantID)
    }

    const handleSearch = (event) => {
        event.preventDefault()
        const formData = new FormData(event.currentTarget)
        formData.forEach((value, key) => {
            searchParams.set(key, value.toString())
        })
        setSearchParams(searchParams)
    }

    const handleRefresh = () => {
        setRefreshId((prevId) => {
            return prevId + 1
        })
    }
    
    const enabledFilter = searchParams.get('enabled') || 'all'
    const paymentsFilter = searchParams.get('can_create_payments') === 'true'
        ? 'on'
        : searchParams.get('can_create_payments') === 'false'
            ? 'off'
            : 'all'
    
    const handleEnabledFilterChange = (event, newValue) => {
        if (newValue === null) return
        const newParams = new URLSearchParams(searchParams)
        if (newValue === 'all') {
            newParams.delete('enabled')
        } else {
            newParams.set('enabled', newValue === 'active' ? 'true' : 'false')
        }
        setSearchParams(newParams)
    }

    const handlePaymentsFilterChange = (event, newValue) => {
        if (newValue === null) return
        const newParams = new URLSearchParams(searchParams)
        if (newValue === 'all') {
            newParams.delete('can_create_payments')
        } else {
            newParams.set('can_create_payments', newValue === 'on' ? 'true' : 'false')
        }
        setSearchParams(newParams)
    }

    if (!merchantsData) return null

    return <React.Fragment>
        <Grid size={12}>
            <PageHeader
                title="Merchants"
                subtitle="Payment access and daily volume limits across all merchants"
                breadcrumbs={[['Dashboard', '/'], ['Merchants', '/merchants']]}
                actions={
                    <>
                        <Button to="/merchants/add" component={AclRouterLink} variant="contained" startIcon={<Add />}>
                            Add Merchant
                        </Button>
                        <Button onClick={handleRefresh} variant="outlined" startIcon={<Refresh />}>
                            Refresh
                        </Button>
                    </>
                }
            />
        </Grid>
        <Grid size={12}>
            <FilterPanel defaultExpanded={accordionExpanded} title="Filters">
                <Box
                    component="form"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                        gap: 2,
                        alignItems: 'end',
                    }}
                    onSubmit={handleSearch}
                    noValidate
                    autoComplete="off"
                >
                    <TextField defaultValue={searchParams.get('email')} id="email" name="email" label="Email"/>
                    <TextField defaultValue={searchParams.get('name')} id="name" name="name" label="Name"/>
                    <TextField defaultValue={searchParams.get('surname')} id="surname" name="surname" label="Surname"/>
                    <TextField defaultValue={searchParams.get('test')} id="test" name="test" label="Test"/>
                    <Stack direction="row" spacing={1.5} sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
                        <Button type="submit" variant="contained" startIcon={<Search />}>
                            Search
                        </Button>
                        <Button onClick={() => setSearchParams({})} variant="outlined" color="inherit" startIcon={<RestartAlt />}>
                            Reset
                        </Button>
                    </Stack>
                </Box>
            </FilterPanel>
        </Grid>
        {merchantsData.payments_access && (
            <Grid size={12}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(7, minmax(0, 1fr))' },
                        gap: 1.5,
                    }}
                >
                    <PaymentsKpi title="Merchants" value={merchantsData.payments_access.total} />
                    <PaymentsKpi title="Payments on" value={merchantsData.payments_access.with_access} />
                    <PaymentsKpi title="Payments off" value={merchantsData.payments_access.without_access} />
                    <PaymentsKpi title="With daily limit" value={merchantsData.payments_access.limited} />
                    <PaymentsKpi title="Unlimited" value={merchantsData.payments_access.unlimited} />
                    <PaymentsKpi
                        title="Total daily limits"
                        value={formatAmount(sumEnabledDailyLimits(merchantsData.payments_access.merchants), currencySymbol)}
                    />
                    <PaymentsKpi
                        title="Today"
                        value={formatAmount(
                            typeof merchantsData.payments_access.today_total === 'number'
                                ? merchantsData.payments_access.today_total
                                : (merchantsData.payments_access.merchants || []).reduce((sum, row) => {
                                    if (!hasPaymentsAccess(row)) return sum
                                    const v = parseFloat(row.day_total || 0)
                                    return Number.isNaN(v) ? sum : sum + v
                                }, 0),
                            currencySymbol
                        )}
                    />
                </Box>
            </Grid>
        )}
        <Grid size={12}>
            <SectionCard sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto'}}>
                <TableToolbar 
                    title="Merchants" 
                    actions={
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <ToggleButtonGroup
                                value={paymentsFilter}
                                size="small"
                                exclusive
                                onChange={handlePaymentsFilterChange}
                                aria-label="Payments access filter"
                            >
                                <ToggleButton value="all">All payments</ToggleButton>
                                <ToggleButton value="on">Payments on</ToggleButton>
                                <ToggleButton value="off">Payments off</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                                value={enabledFilter}
                                size="small"
                                exclusive
                                onChange={handleEnabledFilterChange}
                                aria-label="Status filter"
                            >
                                <ToggleButton value="all">ALL</ToggleButton>
                                <ToggleButton value="active">Active</ToggleButton>
                                <ToggleButton value="inactive">Inactive</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    }
                />
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Username</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Enabled</TableCell>
                            <TableCell>Payments</TableCell>
                            <TableCell>Daily limit</TableCell>
                            <TableCell>Added</TableCell>
                            <TableCell align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {merchantsData.merchants.map((row) => (
                            <TableRow key={`mer-${row.id}`}>
                                <TableCell>
                                    {row.id}
                                </TableCell>
                                <TableCell>
                                    <Link to={`edit?uuid=${helpers.UUIDv4Transform(row.uuid)}`} component={RouterLink}>
                                        {row.username}
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    {row.name}
                                </TableCell>
                                <TableCell>{row.enabled ? <Check  fontSize="small" /> : ''}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={hasPaymentsAccess(row) ? 'On' : 'Off'}
                                        color={hasPaymentsAccess(row) ? 'success' : 'default'}
                                        variant={hasPaymentsAccess(row) ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell>{formatDailyLimit(row, currencySymbol)}</TableCell>
                                <TableCell>{helpers.getDateTime(row.created)}</TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View as merchant">
                                        <IconButton onClick={() => handleSetForceMerchantID(row.id)}>
                                            <Visibility fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View projects">
                                        <IconButton to={`/projects?merchant_id=${row.id}`} component={RouterLink}>
                                            <BusinessCenter fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View transactions">
                                        <IconButton to={`/transactions?merchant_id=${row.id}`} component={RouterLink}>
                                            <FormatListBulleted fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View customers">
                                        <IconButton to={`/customers?merchant_id=${row.id}`} component={RouterLink}>
                                            <People fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Pagination url={'/merchants'} count={merchantsData.count}/>
            </SectionCard>
        </Grid>
    </React.Fragment>
}

